import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "convex/react";
import { api, type Doc, type Id } from "../lib/api";
import { routeIdParam, searchIdParam, searchParamsWithId } from "../domain/routeParams";
import { RichText, type TermInteraction } from "../components/RichText";
import { Button, Icon, JurisBadge, StatusBadge, cx } from "../components/ui";
import {
  buildSectionTree,
  changeTypeStatusBadgeKind,
  filterSectionTree,
  legislationViewerSectionUpdates,
  legislationViewerSelectedSection,
  legislationViewerTabs,
  nextLegislationViewerSectionId,
  sectionAncestors,
  visibleTree,
  type LegislationViewerTabId,
  type SectionTreeNode,
} from "../domain/legislation";
import { definedTermPopoverPlacement } from "../domain/definedTerms";
import { normalizeAmendmentMarkupLayout } from "../domain/markupDoc";
import { toDefinedTermMap, toFlatSection } from "../lib/adapters";
import { useAccess } from "../lib/access";
import { useCompany } from "../lib/company";
import { richTextDocOrEmpty } from "../domain/richText";
import { formatAuDate, formatAuDateTime } from "../domain/time";
import { moveFocusWithin, rovingIndexForKey } from "../lib/accessibility";
import { caughtErrorMessage } from "../domain/errors";

type Tab = LegislationViewerTabId;
type Pop = { term: string; text: string; x: number; y: number } | null;
type SectionNode = SectionTreeNode<Id<"sections">>;

export function LegislationViewer() {
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const id = routeIdParam<"legislation">(params, "id");
  const requestedSectionId = searchIdParam<"sections">(searchParams, "section");

  const { isDev, isStaff, canWrite } = useAccess();
  const { companyId } = useCompany();
  const subscribe = useMutation(api.subscriptions.subscribe);
  const [subscribing, setSubscribing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const searchQuery = searchParams.get("q") ?? "";
  const monitor = async () => {
    if (!id || !companyId || !canWrite) return;
    setSubscribing(true); setNotice(null);
    try { await subscribe({ companyId, legislationId: id }); }
    catch (error) { setNotice(caughtErrorMessage(error, "Could not monitor this law")); }
    finally { setSubscribing(false); }
  };

  const legislation = useQuery(api.legislation.get, id ? { id } : "skip");
  const subs = useQuery(api.subscriptions.listForCompany, companyId !== null ? { companyId } : "skip");

  const openAccess = isDev || isStaff;
  const subscribed = (subs ?? []).some((s) => s.legislationId === id);
  const subsResolved = openAccess || subs !== undefined;
  const canRead = openAccess || subscribed;

  // Only fetch content the caller is entitled to (backend enforces this too).
  const contentArgs = id !== undefined && canRead ? { legislationId: id } : "skip";
  const sections = useQuery(api.sections.listByLegislation, contentArgs);
  const terms = useQuery(api.definedTerms.listByLegislation, contentArgs);
  const updates = useQuery(api.updateLog.listByLegislation, contentArgs);
  const source = useQuery(api.legislation.latestSource, contentArgs);

  const [selectedId, setSelectedId] = useState<Id<"sections"> | null>(null);
  const [tab, setTab] = useState<Tab>("legislation");
  const [treeOpen, setTreeOpen] = useState(() => window.matchMedia("(min-width: 768px)").matches);
  const [filter, setFilter] = useState("");
  const [pop, setPop] = useState<Pop>(null);
  const [citeCopied, setCiteCopied] = useState(false);

  const tree = useMemo(
    () => (sections ? visibleTree(buildSectionTree(sections.map(toFlatSection))) : []),
    [sections],
  );
  const termMap = useMemo(() => toDefinedTermMap(terms ?? []), [terms]);

  // Honor deep links from law-text search, otherwise default to the first leaf section.
  useEffect(() => {
    if (sections === undefined) return;
    const nextSelectedId = nextLegislationViewerSectionId({
      sections,
      tree,
      requestedSectionId,
      selectedId,
    });
    if (nextSelectedId !== selectedId) setSelectedId(nextSelectedId);
  }, [requestedSectionId, sections, selectedId, tree]);

  const interaction = useMemo<TermInteraction>(
    () => ({
      show: (def, el) => {
        const r = el.getBoundingClientRect();
        setPop({ ...def, x: r.left + r.width / 2, y: r.bottom });
      },
      hide: () => setPop(null),
    }),
    [],
  );

  const select = useCallback((sid: Id<"sections">) => {
    setSelectedId(sid);
    setTab("legislation");
    setPop(null);
    if (!window.matchMedia("(min-width: 768px)").matches) setTreeOpen(false);
    setSearchParams(searchParamsWithId(searchParams, "section", sid), { replace: true });
  }, [searchParams, setSearchParams]);

  const selected = legislationViewerSelectedSection({ sections: sections ?? [], selectedId });
  const sectionUpdates = legislationViewerSectionUpdates({
    selectedSectionId: selected?._id ?? null,
    updates: updates ?? [],
  });
  const ancestry = selected ? sectionAncestors({ section: selected, sections: sections ?? [] }) : [];
  const tabs = legislationViewerTabs(sectionUpdates.length);

  const handleTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    const nextIndex = rovingIndexForKey({ key: event.key, currentIndex: index, itemCount: tabs.length, orientation: "horizontal" });
    if (nextIndex === null) return;
    event.preventDefault();
    const nextTab = tabs[nextIndex];
    if (nextTab === undefined) return;
    setTab(nextTab.id);
    window.requestAnimationFrame(() => document.getElementById(`legislation-tab-${nextTab.id}`)?.focus());
  };

  const copyCitation = async () => {
    if (legislation === undefined || legislation === null) return;
    const sectionLabel = selected === null ? "" : `, ${selected.number} ${selected.title}`;
    const sourceLabel = legislation.legacySnapshot !== undefined
      ? `Historical Lawstream snapshot from backup ${legislation.legacySnapshot.capturedAt}; current legal status unverified`
      : source === undefined || source === null
      ? "Lawstream consolidation"
      : `Lawstream consolidation; source captured ${formatAuDate(source.fetchedAt)}`;
    await navigator.clipboard.writeText(
      `${legislation.title} (${legislation.reference})${sectionLabel}. ${sourceLabel}. ${window.location.href}`,
    );
    setCiteCopied(true);
    window.setTimeout(() => setCiteCopied(false), 1800);
  };

  if (legislation === undefined) {
    return <p className="px-9 py-7 text-slate-500">Loading…</p>;
  }
  if (legislation === null) {
    return <p className="px-9 py-7 text-slate-500">Not found. <Link className="text-[var(--accent-dark)]" to="/browse">Back</Link></p>;
  }
  if (!canRead) {
    if (!subsResolved) return <p className="px-9 py-7 text-slate-500">Loading…</p>;
    return <div><SubscribeGate title={legislation.title} jurisdiction={legislation.jurisdiction} reference={legislation.reference} />
      {canWrite && <div className="px-9 pb-7 text-center"><Button disabled={subscribing} onClick={() => void monitor()}>{subscribing ? "Adding…" : "Monitor this law and open reader"}</Button>{notice && <p role="alert">{notice}</p>}</div>}
    </div>;
  }
  if (sections === undefined) {
    return <p className="px-9 py-7 text-slate-500">Loading…</p>;
  }

  return (
    <div className="relative flex h-full min-h-0" onScroll={() => setPop(null)}>
      {treeOpen && (
        <button
          type="button"
          aria-label="Close contents"
          onClick={() => setTreeOpen(false)}
          className="absolute inset-0 z-20 bg-slate-900/25 md:hidden"
        />
      )}
      {/* Tree pane */}
      <div
        id="legislation-contents"
        className={cx(
          "absolute inset-y-0 left-0 z-30 flex w-[min(86vw,300px)] shrink-0 flex-col border-r border-slate-200 bg-slate-50 shadow-xl transition-transform duration-200 md:static md:z-auto md:shadow-none",
          treeOpen
            ? "translate-x-0 md:w-[300px]"
            : "-translate-x-full md:w-0 md:overflow-hidden md:translate-x-0",
        )}
      >
        <div className="border-b border-slate-200/70 px-4 pb-3 pt-4">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Contents</div>
          <div className="flex items-center gap-2 rounded-lg bg-white px-2.5 py-1.5 ring-1 ring-slate-200">
            <Icon name="search" size={14} className="text-slate-400" />
            <input aria-label="Filter contents" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter sections…" className="w-full bg-transparent text-[13px] outline-none placeholder:text-slate-400" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-2.5 py-3">
          {tree.length === 0 ? (
            <p className="p-2 text-[13px] text-slate-400">No sections yet.</p>
          ) : (
            <nav
              aria-label="Legislation contents"
              onKeyDown={(event) => {
                if (moveFocusWithin({ container: event.currentTarget, target: event.target, key: event.key, selector: "[data-hierarchy-item]", orientation: "vertical" })) event.preventDefault();
              }}
            >
              <Tree nodes={filterSectionTree(tree, filter)} depth={0} currentId={selectedId} onSelect={select} />
            </nav>
          )}
        </div>
      </div>

      {/* Content pane */}
      <div className="flex min-w-0 flex-1 flex-col bg-white">
        <div className="border-b border-slate-100 px-4 pb-0 pt-5 sm:px-7 sm:pt-6 lg:px-10">
          <div className="mb-3 flex items-center gap-2">
            <button type="button" aria-label={treeOpen ? "Close contents" : "Open contents"} aria-expanded={treeOpen} aria-controls="legislation-contents" onClick={() => setTreeOpen((v) => !v)} className="-ml-1 grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600" title="Toggle contents">
              <Icon name="layers" size={16} />
            </button>
            <JurisBadge code={legislation.jurisdiction} />
            <span className="truncate font-mono text-[12.5px] text-slate-400">{legislation.reference}</span>
            {ancestry.length > 0 && (
              <>
                <span className="text-slate-300">·</span>
                <span className="truncate text-[12.5px] text-slate-400">{ancestry[0]?.title}</span>
              </>
            )}
          </div>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div className="min-w-0">
              <div className="mb-1 text-[13px] font-medium text-slate-400">{legislation.title}</div>
              <h2 className="font-serif text-[26px] font-semibold leading-tight tracking-tight text-slate-900 lg:text-[30px]">
                {selected ? (
                  <><span className="mr-2 font-normal text-slate-400">{selected.number}</span>{selected.title}</>
                ) : (
                  legislation.title
                )}
              </h2>
            </div>
            <div className="flex flex-wrap items-center gap-2 pb-1">
              {subscribed ? <span className="text-sm font-semibold text-emerald-700">✓ Monitored</span>
                : canWrite && <Button size="sm" variant="outline" disabled={subscribing} onClick={() => void monitor()}>{subscribing ? "Adding…" : "Monitor this law"}</Button>}
              <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-slate-50 px-2.5 py-1.5 text-[12.5px] text-slate-500 ring-1 ring-slate-200">
                <Icon name="shield" size={13} className="text-emerald-600" /> {legislation.legacySnapshot ? "Historical snapshot" : "Lawstream consolidation"}
              </span>
              <Button variant="outline" size="sm" icon="ext" onClick={() => void copyCitation()}>
                {citeCopied ? "Copied" : "Cite"}
              </Button>
            </div>
          </div>
          {notice && <p role="alert" className="mt-2 text-sm text-rose-700">{notice}</p>}
          <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2.5 text-[12.5px] text-slate-600">
            {legislation.legacySnapshot ? <span>Recovered from the {legislation.legacySnapshot.capturedAt} backup. The text may be older than the backup date. Current legal status is unverified; use this dataset for development.</span> : source === undefined ? (
              <span>Loading source provenance...</span>
            ) : source === null ? (
              <span>No source capture is recorded for this Lawstream consolidation.</span>
            ) : (
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span>
                  Source: <span className="font-medium text-slate-800">{source.title}</span> · captured{" "}
                  {formatAuDateTime(source.fetchedAt)}
                </span>
                <a
                  href={source.sourceUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium text-[var(--accent-dark)] hover:underline"
                >
                  Open official source
                </a>
              </div>
            )}
          </div>
          {/* tabs */}
          <div className="-mb-px mt-5 flex items-center gap-1" role="tablist" aria-label="Legislation views">
            {tabs.map((tb, index) => {
              const on = tab === tb.id;
              return (
                <button
                  key={tb.id}
                  id={`legislation-tab-${tb.id}`}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  aria-controls={`legislation-panel-${tb.id}`}
                  tabIndex={on ? 0 : -1}
                  onClick={() => setTab(tb.id)}
                  onKeyDown={(event) => handleTabKeyDown(event, index)}
                  className={cx("relative px-4 py-2.5 text-[13.5px] font-medium transition", on ? "text-[var(--accent-dark)]" : "text-slate-500 hover:text-slate-800")}
                >
                  <span className="inline-flex items-center gap-1.5">
                    {tb.label}
                    {tb.count > 0 && <span className={cx("rounded-full px-1.5 py-0.5 text-[11px] font-semibold leading-none", on ? "bg-[var(--accent-soft)] text-[var(--accent-dark)]" : "bg-slate-100 text-slate-500")}>{tb.count}</span>}
                  </span>
                  {on && <span className="absolute -bottom-px left-2 right-2 h-[2.5px] rounded-full" style={{ background: "var(--accent)" }} />}
                </button>
              );
            })}
          </div>
        </div>

        {/* body */}
        <div className="flex-1 overflow-y-auto" onScroll={() => setPop(null)}>
          <div
            id={`legislation-panel-${tab}`}
            role="tabpanel"
            aria-labelledby={`legislation-tab-${tab}`}
            tabIndex={0}
            className="mx-auto max-w-[720px] px-4 py-7 sm:px-7 sm:py-8 lg:px-10"
          >
            {selected === null ? (
              <p className="text-slate-400">Select a section from the contents.</p>
            ) : tab === "legislation" ? (
              <div className="ls-legal">
                <RichText doc={richTextDocOrEmpty(selected.content)} terms={termMap} interaction={interaction} searchQuery={searchQuery} legal />
              </div>
            ) : (
              <UpdatesTab entries={sectionUpdates} />
            )}
            <div className="mt-12 flex items-center justify-between border-t border-slate-100 pt-5 text-[12.5px] text-slate-400">
              <span>Lawstream · {legislation.legacySnapshot ? "historical development snapshot" : "consolidated text"}</span>
              {selected && <span className="font-mono">{selected.number}</span>}
            </div>
          </div>
        </div>
      </div>

      <TermPopover pop={pop} onClose={() => setPop(null)} />
    </div>
  );
}

function SubscribeGate({ title, jurisdiction, reference }: { title: string; jurisdiction: Doc<"legislation">["jurisdiction"]; reference: string }) {
  return (
    <div className="mx-auto max-w-[560px] px-6 py-16 text-center">
      <div className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-full bg-[var(--accent-soft)] text-[var(--accent-dark)]">
        <Icon name="lock" size={22} />
      </div>
      <div className="mb-2 flex items-center justify-center gap-2">
        <JurisBadge code={jurisdiction} />
        <span className="font-mono text-[12.5px] text-slate-400">{reference}</span>
      </div>
      <h2 className="font-serif text-[22px] font-semibold text-slate-900">{title}</h2>
      <p className="mt-2 text-sm text-slate-500">
        Your organisation isn't subscribed to this legislation, so its consolidated text and updates are locked.
      </p>
      <div className="mt-5 flex justify-center gap-2">
        <Link to="/subscriptions"><Button icon="plus">Subscribe to read</Button></Link>
        <Link to="/browse"><Button variant="outline">Back to catalogue</Button></Link>
      </div>
    </div>
  );
}

function UpdatesTab({ entries }: { entries: Doc<"updateLog">[] }) {
  if (entries.length === 0) {
    return (
      <div className="py-20 text-center">
        <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full bg-slate-100 text-slate-400"><Icon name="check" size={22} /></div>
        <p className="text-sm text-slate-500">No recorded amendments for this section.</p>
      </div>
    );
  }
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 pb-1 text-[12.5px] text-slate-500">
        <span className="inline-flex items-center gap-1.5"><span className="ls-key-ins" />Inserted text</span>
        <span className="inline-flex items-center gap-1.5"><span className="ls-key-del" />Repealed / deleted</span>
        <span className="ml-auto text-slate-400">{entries.length} amendment{entries.length === 1 ? "" : "s"} recorded</span>
      </div>

      {entries.map((a) => {
        const markup = a.markup == null ? null : normalizeAmendmentMarkupLayout(richTextDocOrEmpty(a.markup));
        return (
          <div key={a._id} className="overflow-hidden rounded-xl bg-white ring-1 ring-slate-200">
            <div className="border-b border-slate-200 bg-slate-50/70 px-5 py-3.5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[14px] font-medium text-slate-800">{a.amendingLegislation}</div>
                  {a.summary !== null && <div className="mt-0.5 text-[12.5px] text-slate-500">{a.summary}</div>}
                </div>
                <StatusBadge kind={changeTypeStatusBadgeKind(a.changeType)} />
              </div>
              <div className="mt-2 flex items-center gap-1.5 text-[12.5px] text-slate-500">
                <Icon name="clock" size={13} /> Commenced <span className="font-medium text-slate-700">{formatAuDate(a.commencementDate)}</span>
              </div>
            </div>
            {markup !== null && (
              <div className="px-5 py-4">
                <div className="ls-legal text-[15px]"><RichText doc={markup} legal /></div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function TermPopover({ pop, onClose }: { pop: Pop; onClose: () => void }) {
  if (pop === null) return null;
  const placement = definedTermPopoverPlacement({
    anchorX: pop.x,
    anchorY: pop.y,
    popoverWidth: 340,
    viewportWidth: window.innerWidth,
  });
  return (
    <div className="fixed z-50 animate-[fadeIn_120ms_ease-out]" style={placement} onMouseLeave={onClose}>
      <div className="rounded-xl bg-slate-900 p-3.5 text-slate-100 shadow-xl ring-1 ring-black/10">
        <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--accent-light)]">Defined term</div>
        <div className="mb-1 font-serif text-[15px] font-semibold leading-snug">{pop.term}</div>
        <p className="text-[12.5px] leading-relaxed text-slate-300">{pop.text}</p>
      </div>
    </div>
  );
}

function Tree({ nodes, depth, currentId, onSelect }: { nodes: readonly SectionNode[]; depth: number; currentId: Id<"sections"> | null; onSelect: (id: Id<"sections">) => void }) {
  return (
    <div className="space-y-0.5">
      {nodes.map((n) => {
        const isLeaf = n.nodeType === "section";
        const active = n.id === currentId;
        return (
          <div key={n.id}>
            <button
              type="button"
              data-hierarchy-item
              aria-current={active ? "location" : undefined}
              onClick={() => isLeaf && onSelect(n.id)}
              disabled={!isLeaf}
              style={{ paddingLeft: 10 + depth * 14 }}
              className={cx("group relative flex w-full items-center gap-1.5 rounded-md py-[7px] pr-2 text-left transition", active ? "bg-[var(--accent-soft)]" : isLeaf ? "hover:bg-slate-100" : "")}
            >
              {active && <span className="absolute bottom-1 left-0 top-1 w-[3px] rounded-r" style={{ background: "var(--accent)" }} />}
              {isLeaf ? (
                <span className={cx("w-6 shrink-0 font-mono text-[11px] tabular-nums", active ? "text-[var(--accent-dark)]" : "text-slate-400")}>{n.number}</span>
              ) : (
                <span className="w-3.5 shrink-0" />
              )}
              <span className={cx("truncate", n.nodeType === "part" ? "text-[12.5px] font-semibold uppercase tracking-wide text-slate-700" : active ? "text-[13px] font-medium text-[var(--accent-dark)]" : "text-[13px] text-slate-600 group-hover:text-slate-900")}>
                {n.title}
              </span>
            </button>
            {n.children.length > 0 && <Tree nodes={n.children} depth={depth + 1} currentId={currentId} onSelect={onSelect} />}
          </div>
        );
      })}
    </div>
  );
}
